"use client";
import {
  __async
} from "../../../chunk-MMUBH76A.js";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { CpslCodeInput, CpslSpinner, CpslText } from "@getpara/react-components";
import { useEffect, useRef, useState } from "react";
import { styled } from "styled-components";
import { ModalStep } from "../../utils/steps.js";
import { useModalStore, useThemeStore, useUserInfoStore } from "../../stores/index.js";
import { Heading, InnerStepContainer, StepContainer } from "../common.js";
import { AuthMethod } from "@getpara/core-sdk";
import { useInternalClient } from "../../../provider/hooks/utils/useInternalClient.js";
import { useUserAgent } from "@getpara/react-common";
const VerificationCodeStep = () => {
  const userAgent = useUserAgent();
  const theme = useThemeStore((state) => state.theme);
  const authInfo = useUserInfoStore((state) => state.getAuthInfo());
  const isPasskeySupported = useModalStore((state) => state.isPasskeySupported);
  const setStep = useModalStore((state) => state.setStep);
  const setWebAuthURLForCreate = useModalStore((state) => state.setWebAuthURLForCreate);
  const setIFrameUrl = useModalStore((state) => state.setIFrameUrl);
  const setIsIFrameReady = useModalStore((state) => state.setIsIFrameReady);
  const setAuthStepRoute = useModalStore((state) => state.setAuthStepRoute);
  const para = useInternalClient();
  const inputRef = useRef(null);
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState("");
  const [resendStatus, setResendStatus] = useState("Resend.");
  const [resendDisabled, setResendDisabled] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const isEmail = (authInfo == null ? void 0 : authInfo.authType) === "email";
  useEffect(() => {
    setTimeout(() => {
      var _a, _b;
      (_b = (_a = inputRef.current.shadowRoot.querySelectorAll("input")) == null ? void 0 : _a[0]) == null ? void 0 : _b.focus();
    }, 10);
  }, []);
  useEffect(() => {
    if (code.length === 6) {
      handleSubmitCode();
    }
  }, [code]);
  const handleResendClick = () => __async(void 0, null, function* () {
    if (!resendDisabled) {
      setResendStatus("Resent!");
      setResendDisabled(true);
      isEmail ? yield para.resendVerificationCode() : yield para.resendVerificationCodeByPhone();
      setTimeout(() => {
        setResendStatus("Resend.");
        setResendDisabled(false);
      }, 3e3);
    }
  });
  const handleCodeInput = (e) => {
    if (codeError) {
      setCodeError("");
    }
    setCode(e.detail.value.trim());
  };
  const handleSubmitCode = () => __async(void 0, null, function* () {
    setIsVerifying(true);
    if (code.length === 6 && /^\d+$/.test(code)) {
      try {
        const supportedCreateAuthMethods = yield para.getSupportedCreateAuthMethods();
        const supportsPasskey = supportedCreateAuthMethods.has(AuthMethod.PASSKEY);
        const supportsPassword = supportedCreateAuthMethods.has(AuthMethod.PASSWORD) || !isPasskeySupported;
        const isMobileNoPasskey = !isPasskeySupported && (userAgent == null ? void 0 : userAgent.device.type) === "mobile";
        if (supportsPasskey && supportsPassword && !isMobileNoPasskey) {
          setIsIFrameReady(false);
          const webAuthUrl = isEmail ? yield para.verifyEmail({ verificationCode: code }) : yield para.verifyPhone({ verificationCode: code });
          const passwordAuthUrl = yield para.getSetupPasswordURL({ authType: authInfo == null ? void 0 : authInfo.authType, theme });
          setWebAuthURLForCreate(yield para.shortenLoginLink(webAuthUrl));
          setIFrameUrl(yield para.shortenLoginLink(passwordAuthUrl));
          setAuthStepRoute(ModalStep.BIOMETRIC_CREATION);
          return;
        } else if (supportsPassword) {
          setIsIFrameReady(false);
          isEmail ? yield para.verifyEmail({ verificationCode: code }) : yield para.verifyPhone({ verificationCode: code });
          const url = yield para.getSetupPasswordURL({ authType: authInfo == null ? void 0 : authInfo.authType, theme });
          setIFrameUrl(yield para.shortenLoginLink(url));
          setAuthStepRoute(ModalStep.PASSWORD_CREATION);
          return;
        } else if (!isMobileNoPasskey) {
          const url = isEmail ? yield para.verifyEmail({ verificationCode: code }) : yield para.verifyPhone({ verificationCode: code });
          setWebAuthURLForCreate(yield para.shortenLoginLink(url));
          setStep(ModalStep.BIOMETRIC_CREATION);
        }
      } catch (e) {
        if (e.message.includes("429")) {
          setCodeError("Too many incorrect attempts. Please try again in 10 minutes.");
        } else {
          setCodeError("Incorrect code.");
        }
      }
    } else {
      setCodeError("Incorrect code.");
    }
    setIsVerifying(false);
  });
  return /* @__PURE__ */ jsxs(StepContainer, { $wide: true, children: [
    /* @__PURE__ */ jsxs(InnerStepContainer, { children: [
      /* @__PURE__ */ jsxs(Heading, { variant: "headingS", weight: "bold", children: [
        "Verify ",
        isEmail ? "Email" : "Phone Number"
      ] }),
      /* @__PURE__ */ jsxs(InlineText, { variant: "bodyS", color: "secondary", children: [
        "Please enter the code we sent to ",
        /* @__PURE__ */ jsx(InlineText, { variant: "bodyS", children: authInfo == null ? void 0 : authInfo.identifier })
      ] })
    ] }),
    /* @__PURE__ */ jsx(InnerStepContainer, { children: isVerifying ? /* @__PURE__ */ jsx(CpslSpinner, {}) : /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(
        "form",
        {
          onSubmit: (e) => __async(void 0, null, function* () {
            e.preventDefault();
            yield handleSubmitCode();
          }),
          children: /* @__PURE__ */ jsx(
            StyledCodeInput,
            {
              ref: inputRef,
              length: 6,
              type: "number",
              code,
              onCpslInput: handleCodeInput,
              errorText: codeError,
              onKeyDown: (e) => __async(void 0, null, function* () {
                return e.key === "Enter" && (yield handleSubmitCode());
              })
            }
          )
        }
      ),
      /* @__PURE__ */ jsxs(InlineText, { variant: "bodyS", color: "secondary", children: [
        "Didn\u2019t receive a code?",
        " ",
        /* @__PURE__ */ jsx(
          ClickableText,
          {
            variant: "bodyS",
            style: { cursor: resendDisabled ? "default" : "pointer" },
            onClick: handleResendClick,
            children: resendStatus
          }
        )
      ] })
    ] }) })
  ] });
};
const StyledCodeInput = styled(CpslCodeInput)`
  align-self: center;
`;
const InlineText = styled(CpslText)`
  text-align: center;
  display: inline-block;
`;
const ClickableText = styled(InlineText)`
  cursor: pointer;
  display: inline-block;
`;
export {
  VerificationCodeStep
};
